import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  BarChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
} from "recharts";
import PageHeader from "../components/layout/PageHeader";
import ProjectSelector from "../components/ProjectSelector";
import KpiCard from "../components/ui/KpiCard";
import ChartCard from "../components/ui/ChartCard";
import StatusBadge from "../components/ui/StatusBadge";
import FlagChip from "../components/ui/FlagChip";
import DataTable from "../components/ui/DataTable";
import { EmptyState, ErrorState, CardSkeleton, KpiSkeletonRow } from "../components/ui/states";
import { MoneyTooltip } from "../components/ui/MoneyTooltip";
import { useApi } from "../lib/useApi";
import { api, qs, ApiClientError } from "../lib/api";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "../components/ui/Toast";
import { formatUSD, formatPct, formatDate, signClass } from "../lib/format";
import { CHART } from "../lib/chart";
import { CLASS_LABEL, CLASS_COLOR } from "../lib/cashflow";
import type {
  Project,
  ProjectDetail,
  CashFlowResponse,
  CapitalStructureResponse,
  CashFlowClassTotal,
  Flag,
  SovLine,
  ReconciliationItem,
} from "@shared/types";

const sovCol = createColumnHelper<SovLine>();
const ledgerCol = createColumnHelper<ReconciliationItem>();

export default function ProjectCashFlow() {
  const { activeClientId, isBtzl } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const params = useParams();
  const raw = params.projectId;
  const isAll = raw === "all";
  const urlId = raw && raw !== "all" ? Number(raw) : null;

  const projectsState = useApi<{ projects: Project[] }>(`/api/projects${qs({ clientId: activeClientId })}`);
  const projects = projectsState.data?.projects ?? [];
  const selectedId = isAll ? null : urlId ?? projects[0]?.id ?? null;

  // Land on the first project if nothing is chosen (and not the all-projects view).
  useEffect(() => {
    if (!raw && projects.length > 0) navigate(`/cash-flow/${projects[0].id}`, { replace: true });
  }, [raw, projects, navigate]);

  const detail = useApi<ProjectDetail>(!isAll && selectedId ? `/api/projects/${selectedId}` : null);
  const cash = useApi<CashFlowResponse>(
    isAll
      ? `/api/portfolio/cashflow${qs({ clientId: activeClientId })}`
      : selectedId
      ? `/api/projects/${selectedId}/cashflow`
      : null
  );
  const capital = useApi<CapitalStructureResponse>(`/api/portfolio/capital-structure${qs({ clientId: activeClientId })}`);
  const flagsState = useApi<{ flags: Flag[] }>(!isAll && selectedId ? `/api/projects/${selectedId}/flags` : null);

  const [movementFilter, setMovementFilter] = useState<string>("all");

  async function closeFlag(flag: Flag) {
    try {
      await api.patch(`/api/flags/${encodeURIComponent(String(flag.id))}`, { status: "closed" });
      toast.success("Flag closed");
      flagsState.refetch();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not close flag");
    }
  }

  const sovColumns = useMemo(
    () => [
      sovCol.accessor("category", { header: "Category", cell: (c) => <span className="font-medium">{c.getValue()}</span> }),
      sovCol.accessor("estimate", { header: "Estimate", meta: { numeric: true }, cell: (c) => formatUSD(c.getValue()) }),
      sovCol.accessor("budget", { header: "Budget", meta: { numeric: true }, cell: (c) => formatUSD(c.getValue()) }),
      sovCol.accessor("actualToDate", { header: "Actual", meta: { numeric: true }, cell: (c) => formatUSD(c.getValue()) }),
      sovCol.accessor("percentComplete", { header: "% Complete", meta: { numeric: true }, cell: (c) => formatPct(c.getValue()) }),
      sovCol.accessor("confidence", { header: "Confidence", cell: (c) => <StatusBadge status={c.getValue()} /> }),
    ],
    []
  );

  const ledger = cash.data?.ledger ?? [];
  const movementTypes = useMemo(() => Array.from(new Set(ledger.map((l) => l.movementType))).sort(), [ledger]);
  const filteredLedger = useMemo(
    () => (movementFilter === "all" ? ledger : ledger.filter((l) => l.movementType === movementFilter)),
    [ledger, movementFilter]
  );

  const ledgerColumns = useMemo(
    () => [
      ledgerCol.accessor("date", { header: "Date", cell: (c) => formatDate(c.getValue()) }),
      ledgerCol.accessor("movementType", { header: "Type" }),
      ledgerCol.accessor("counterparty", { header: "Counterparty", cell: (c) => c.getValue() ?? "—" }),
      ledgerCol.accessor("sovStage", { header: "Stage", cell: (c) => c.getValue() ?? "—" }),
      ledgerCol.accessor("cashImpact", {
        header: "Cash impact",
        meta: { numeric: true },
        cell: (c) => <span className={signClass(c.getValue())}>{formatUSD(c.getValue())}</span>,
      }),
      ledgerCol.accessor("matchStatus", { header: "Status", cell: (c) => <StatusBadge status={c.getValue()} /> }),
    ],
    []
  );

  const project = detail.data?.project;
  const budget = (detail.data?.sovLines ?? []).reduce((s, l) => s + (l.budget ?? 0), 0);
  const spent = (detail.data?.sovLines ?? []).reduce((s, l) => s + (l.actualToDate ?? 0), 0);
  const net = cash.data?.series.reduce((s, w) => s + w.net, 0) ?? 0;

  function onSelect(id: number | "all") {
    navigate(id === "all" ? `/cash-flow/all` : `/cash-flow/${id}`);
  }

  if (projectsState.loading) {
    return (
      <>
        <PageHeader title="Detailed Cash Flow" />
        <KpiSkeletonRow count={4} />
      </>
    );
  }
  if (projectsState.error) {
    return (
      <>
        <PageHeader title="Detailed Cash Flow" />
        <ErrorState message={projectsState.error} onRetry={projectsState.refetch} />
      </>
    );
  }
  if (projects.length === 0) {
    return (
      <>
        <PageHeader title="Detailed Cash Flow" />
        <EmptyState title="No projects" message="There are no projects in the selected scope." />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Detailed Cash Flow"
        subtitle={
          isAll
            ? "All projects combined"
            : project
            ? `${project.location ?? ""}${project.type ? ` · ${project.type}` : ""}`
            : undefined
        }
        actions={
          <ProjectSelector projects={projects} value={isAll ? "all" : selectedId} onChange={onSelect} includeAll />
        }
      />

      {/* Flags (per-project only) */}
      {!isAll && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {flagsState.loading ? (
            <div className="skeleton h-6 w-48" />
          ) : (flagsState.data?.flags.length ?? 0) === 0 ? (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-positive/20 bg-positive-50 px-2.5 py-1 text-xs font-medium text-positive-700">
              <span className="h-1.5 w-1.5 rounded-full bg-positive" /> No active flags
            </span>
          ) : (
            flagsState.data?.flags.map((f) => (
              <FlagChip key={String(f.id)} flag={f} onClose={isBtzl ? closeFlag : undefined} />
            ))
          )}
        </div>
      )}

      {/* KPI mini-row */}
      {(!isAll && detail.loading) || cash.loading ? (
        <KpiSkeletonRow count={4} />
      ) : (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {isAll ? (
            <>
              <KpiCard label="Net cash flow" value={<span className={signClass(net)}>{formatUSD(net, { compact: true })}</span>} sub={`${cash.data?.series.length ?? 0} weeks`} />
              <KpiCard label="Debt (committed)" value={formatUSD(capital.data?.debtTotal ?? 0, { compact: true })} sub="Capital Structure" />
              <KpiCard label="Equity (committed)" value={formatUSD(capital.data?.equityTotal ?? 0, { compact: true })} sub="Capital Structure" />
              <KpiCard label="Projects" value={projects.length} sub="combined" />
            </>
          ) : (
            <>
              <KpiCard label="Budget" value={formatUSD(budget, { compact: true })} sub={project?.status ? <StatusBadge status={project.status} /> : undefined} />
              <KpiCard label="Spent to date" value={formatUSD(spent, { compact: true })} sub={formatPct(budget > 0 ? spent / budget : 0) + " utilized"} accent={spent > budget ? "critical" : undefined} />
              <KpiCard label="Net cash flow" value={<span className={signClass(net)}>{cash.data?.hasDetail ? formatUSD(net, { compact: true }) : "—"}</span>} sub={cash.data?.hasDetail ? `${cash.data.series.length} weeks` : "no detail"} />
              <KpiCard label="Debt + equity" value={formatUSD(capital.data?.total ?? 0, { compact: true })} sub={`${formatUSD(capital.data?.debtTotal ?? 0, { compact: true })} debt`} />
            </>
          )}
        </div>
      )}

      {/* Weekly cash flow + Operating/Financing/Investing breakdown */}
      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <ChartCard title="Weekly cash flow" subtitle="Inflows, outflows, net and cumulative position" className="lg:col-span-2">
          {cash.loading ? (
            <div className="skeleton h-64 w-full" />
          ) : !cash.data?.hasDetail ? (
            <EmptyState
              title="No weekly cash flow yet"
              message="There is no weekly cash flow in this scope."
              icon="≈"
            />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={cash.data.series} margin={{ left: 8, right: 8, top: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} vertical={false} />
                <XAxis dataKey="weekIndex" tickFormatter={(v) => `W${v}`} tick={CHART.tick} stroke={CHART.axis} />
                <YAxis tickFormatter={(v) => formatUSD(v, { compact: true })} tick={CHART.tick} stroke={CHART.axis} width={56} />
                <Tooltip content={<MoneyTooltip />} />
                <Legend wrapperStyle={CHART.legend} />
                <Bar dataKey="inflow" name="Inflows" fill={CHART.positive} />
                <Bar dataKey="outflow" name="Outflows" fill={CHART.critical} />
                <Line dataKey="cumulative" name="Cumulative cash" stroke={CHART.accent} strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="By cash-flow type" subtitle="Operating · Financing · Investing">
          {cash.loading ? <div className="skeleton h-64 w-full" /> : <OfiBreakdown byClass={cash.data?.byClass ?? []} />}
        </ChartCard>
      </div>

      {/* Debt vs Equity (client capital structure) */}
      <ChartCard title="Debt vs Equity" subtitle="Committed capital structure (from the Capital Structure sheet)" className="mt-4">
        {capital.loading ? (
          <div className="skeleton h-56 w-full" />
        ) : (capital.data?.total ?? 0) === 0 ? (
          <EmptyState title="No capital structure" message="No financing movements have been recorded." icon="$" />
        ) : (
          <div className="grid gap-4 lg:grid-cols-2">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart
                data={[
                  { k: "Debt", value: capital.data!.debtTotal, fill: CLASS_COLOR.financing },
                  { k: "Equity", value: capital.data!.equityTotal, fill: CHART.positive },
                ]}
                margin={{ left: 8, right: 8, top: 8 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} vertical={false} />
                <XAxis dataKey="k" tick={CHART.tick} stroke={CHART.axis} />
                <YAxis tickFormatter={(v) => formatUSD(v, { compact: true })} tick={CHART.tick} stroke={CHART.axis} width={56} />
                <Tooltip content={<MoneyTooltip />} />
                <Bar dataKey="value" name="Committed" radius={[2, 2, 0, 0]}>
                  <Cell fill={CLASS_COLOR.financing} />
                  <Cell fill={CHART.positive} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="overflow-hidden rounded-lg border border-ink-200">
              <table className="w-full text-sm">
                <thead className="bg-ink-50 text-xs uppercase text-ink-500">
                  <tr><th className="px-3 py-2 text-left">Category</th><th className="px-3 py-2 text-left">Kind</th><th className="px-3 py-2 text-right">Amount</th></tr>
                </thead>
                <tbody className="divide-y divide-ink-100">
                  {capital.data!.rows.map((row) => (
                    <tr key={row.id}>
                      <td className="px-3 py-2 font-medium text-ink-800">{row.category}</td>
                      <td className="px-3 py-2 capitalize text-ink-600">{row.kind}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatUSD(row.amount)}</td>
                    </tr>
                  ))}
                  <tr className="bg-ink-50 font-semibold">
                    <td className="px-3 py-2" colSpan={2}>Total</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatUSD(capital.data!.total)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </ChartCard>

      {/* Per-project: SOV by category + ledger detail */}
      {!isAll && (
        <>
          <div className="mt-4">
            <h3 className="mb-2 text-sm font-semibold text-ink-800">Schedule of Values — budget by category</h3>
            {detail.loading ? <CardSkeleton rows={6} /> : (
              <DataTable data={detail.data?.sovLines ?? []} columns={sovColumns} stickyFirstColumn initialSort={[{ id: "budget", desc: true }]} />
            )}
          </div>

          <div className="mt-4">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-ink-800">Cash flow detail (ledger)</h3>
              {ledger.length > 0 && (
                <select
                  value={movementFilter}
                  onChange={(e) => setMovementFilter(e.target.value)}
                  className="rounded-lg border border-ink-300 bg-white px-3 py-1.5 text-sm focus-ring"
                >
                  <option value="all">All movement types</option>
                  {movementTypes.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              )}
            </div>
            {cash.loading ? <CardSkeleton rows={6} /> : ledger.length === 0 ? (
              <EmptyState title="No ledger transactions" message="This project has no transaction-level detail." icon="⇄" />
            ) : (
              <DataTable data={filteredLedger} columns={ledgerColumns} maxHeight="28rem" emptyMessage="No transactions match the filter" />
            )}
          </div>
        </>
      )}
    </>
  );
}

/* ------------------ Operating / Financing / Investing panel ---------------- */

function OfiBreakdown({ byClass }: { byClass: CashFlowClassTotal[] }) {
  const order: CashFlowClassTotal["cls"][] = ["operating", "financing", "investing"];
  const map = new Map(byClass.map((c) => [c.cls, c]));
  return (
    <div className="space-y-3">
      {order.map((cls) => {
        const c = map.get(cls) ?? { cls, inflow: 0, outflow: 0, net: 0 };
        return (
          <div key={cls} className="rounded-lg border border-ink-100 bg-ink-50/50 p-3">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-sm font-medium text-ink-800">
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: CLASS_COLOR[cls] }} />
                {CLASS_LABEL[cls]}
              </span>
              <span className={`text-sm font-semibold tabular-nums ${signClass(c.net)}`}>{formatUSD(c.net)}</span>
            </div>
            <div className="mt-1 flex justify-between text-xs text-ink-500">
              <span>In {formatUSD(c.inflow)}</span>
              <span>Out {formatUSD(c.outflow)}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
